import { SteadoLoader } from '@/components/shell/SteadoLoader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

export interface RequestResolvePanelProps {
  /** The catalog row just created. */
  readonly name: string;
  /** The exercise request it was created for. */
  readonly requestName: string;
  readonly pending: boolean;
  /** Why the resolve failed, or null while it runs. */
  readonly error: string | null;
  readonly onRetry: () => void;
  readonly onOpen: () => void;
}

/**
 * In place of the create form once a row made from an exercise request
 * exists: the request is resolved as a second call, and the form must not be
 * offered again, or a retry would create the row twice. A failed resolve
 * stays here, says so, and tries again on request. Until it succeeds, scans
 * of the request's name are still treated as a gap.
 */
export function RequestResolvePanel({
  name,
  requestName,
  pending,
  error,
  onRetry,
  onOpen,
}: RequestResolvePanelProps) {
  if (error === null) {
    return (
      <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
        <SteadoLoader />
        Resolving the request “{requestName}” to “{name}”…
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <Alert role="alert" tone="err">
        <AlertTitle>“{name}” was created, but the request was not resolved</AlertTitle>
        <AlertDescription>
          “{requestName}” is still open, so scans of that name still get an unreviewed exercise: {error}
        </AlertDescription>
      </Alert>
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={onRetry} disabled={pending}>
          {pending ? 'Resolving…' : 'Try resolving again'}
        </Button>
        <Button type="button" variant="outline" onClick={onOpen} disabled={pending}>
          Open the new exercise
        </Button>
      </div>
    </div>
  );
}
